import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pointsTableAPI, tournamentsAPI, reportsAPI } from '../services/api';

export default function PointsTable() {
  const [selectedTournament, setSelectedTournament] = useState('');
  const queryClient = useQueryClient();

  const { data: tournaments = [] } = useQuery({
    queryKey: ['tournaments'],
    queryFn: () => tournamentsAPI.getTournaments(),
  });

  const { data: pointsTable, isLoading, error } = useQuery({
    queryKey: ['pointsTable', selectedTournament],
    queryFn: () => pointsTableAPI.getPointsTable(selectedTournament),
    enabled: !!selectedTournament,
    retry: false,
  });

  const initMutation = useMutation({
    mutationFn: pointsTableAPI.initializePointsTable,
    onSuccess: () => queryClient.invalidateQueries(['pointsTable', selectedTournament]),
  });

  const updateMutation = useMutation({
    mutationFn: pointsTableAPI.updatePointsTable,
    onSuccess: () => queryClient.invalidateQueries(['pointsTable', selectedTournament]),
  });

  const getPositionChange = (standing) => {
    if (!standing.previous_position || standing.previous_position === standing.position) {
      return null;
    }
    const change = standing.previous_position - standing.position;
    if (change > 0) {
      return <span className="text-green-600 ml-1">▲{change}</span>;
    }
    return <span className="text-red-600 ml-1">▼{Math.abs(change)}</span>;
  };

  const getFormBadge = (result) => {
    switch (result) {
      case 'W': return <span className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold">W</span>;
      case 'L': return <span className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center text-xs font-bold">L</span>;
      case 'T': return <span className="w-6 h-6 rounded-full bg-yellow-500 text-white flex items-center justify-center text-xs font-bold">T</span>;
      case 'NR': return <span className="w-6 h-6 rounded-full bg-gray-400 text-white flex items-center justify-center text-xs font-bold">N</span>;
      default: return null;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Points Table</h1>

      <div className="mb-6 space-y-4">
        <select
          value={selectedTournament}
          onChange={(e) => setSelectedTournament(e.target.value)}
          className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md"
        >
          <option value="">Select Tournament</option>
          {tournaments.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>

        {selectedTournament && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => initMutation.mutate(selectedTournament)}
              disabled={initMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {initMutation.isPending ? 'Initializing...' : 'Initialize'}
            </button>
            <button
              onClick={() => updateMutation.mutate(selectedTournament)}
              disabled={updateMutation.isPending}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm"
            >
              {updateMutation.isPending ? 'Updating...' : 'Recalculate'}
            </button>
            <button
              onClick={() => reportsAPI.exportPointsTableCSV(selectedTournament)}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm"
            >
              Export CSV
            </button>
          </div>
        )}
      </div>

      {error && error.response?.status === 404 && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded mb-6">
          Points table not found. Click "Initialize" to create one.
        </div>
      )}

      {isLoading && <p>Loading points table...</p>}

      {pointsTable && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b">
            <h2 className="text-lg font-semibold">{pointsTable.tournament_name}</h2>
            <p className="text-sm text-gray-500">
              Last updated: {new Date(pointsTable.last_updated).toLocaleString()}
            </p>
          </div>

          {pointsTable.is_group_stage && pointsTable.groups?.length > 0 ? (
            // Group stage view
            <div className="space-y-6 p-6">
              {pointsTable.groups.map((group) => (
                <div key={group.group_id} className="border rounded-lg overflow-hidden">
                  <h3 className="px-4 py-2 bg-gray-100 font-semibold">{group.group_name}</h3>
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pos</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Team</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">P</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">W</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">L</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Pts</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">NRR</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {group.teams.map((team, idx) => (
                        <tr key={team.team_id} className={idx < 2 ? 'bg-green-50' : ''}>
                          <td className="px-4 py-2 text-sm">{team.position}</td>
                          <td className="px-4 py-2 font-medium">{team.team_name}</td>
                          <td className="px-4 py-2 text-center text-sm">{team.matches_played}</td>
                          <td className="px-4 py-2 text-center text-sm">{team.matches_won}</td>
                          <td className="px-4 py-2 text-center text-sm">{team.matches_lost}</td>
                          <td className="px-4 py-2 text-center font-semibold">{team.points}</td>
                          <td className="px-4 py-2 text-center text-sm">
                            {team.net_run_rate >= 0 ? '+' : ''}{team.net_run_rate?.toFixed(3)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          ) : (
            // League view
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pos</th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team</th>
                    <th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">P</th>
                    <th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">W</th>
                    <th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">L</th>
                    <th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">T</th>
                    <th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">NR</th>
                    <th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Pts</th>
                    <th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">NRR</th>
                    <th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Form</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {pointsTable.standings?.map((standing, idx) => (
                    <tr key={standing.team_id} className={idx < 4 ? 'bg-green-50' : ''}>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap">
                        <span className="font-semibold">{standing.position}</span>
                        {getPositionChange(standing)}
                      </td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap font-medium text-sm sm:text-base">{standing.team_name}</td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap text-center text-sm">{standing.matches_played}</td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap text-center text-green-600 font-medium text-sm">{standing.matches_won}</td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap text-center text-red-600 text-sm">{standing.matches_lost}</td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap text-center text-sm hidden sm:table-cell">{standing.matches_tied}</td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap text-center text-sm hidden sm:table-cell">{standing.matches_no_result}</td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap text-center font-bold text-base sm:text-lg">{standing.points}</td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap text-center text-sm">
                        <span className={standing.net_run_rate >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {standing.net_run_rate >= 0 ? '+' : ''}{standing.net_run_rate?.toFixed(3)}
                        </span>
                      </td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap hidden md:table-cell">
                        <div className="flex space-x-1">
                          {standing.recent_form?.map((result, i) => (
                            <span key={i}>{getFormBadge(result)}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
